import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { dealsApi } from "@/api/endpoints";
import type { DealDetail, Signer } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Note } from "@/components/ui/Note";
import { ActionHint, Panel, PanelActions, PanelHead, Spacer } from "@/components/ui/Panel";
import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/cn";
import { initials } from "@/lib/format";

interface StepProps {
  deal: DealDetail;
  /** Someone else's Vorgang: show it, but offer nothing that writes. */
  readOnly: boolean;
  onChanged: (deal: DealDetail) => void;
  onNext: () => void;
  onBack: () => void;
}

const ZOHO_GRACE_SECONDS = 10;

export function SignatureStep({ deal, readOnly, onChanged, onNext, onBack }: StepProps) {
  const { t, formatDateTime } = useI18n();
  const request = deal.signature_request;
  const complete = request?.status === "completed";
  const [grace, setGrace] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(ZOHO_GRACE_SECONDS);
  const sendStartedRef = useRef(false);

  async function reload() {
    onChanged(await dealsApi.detail(deal.id));
  }

  const send = useMutation({
    mutationFn: (simulate: boolean) => dealsApi.sendSignature(deal.id, simulate),
    onSuccess: reload,
    onSettled: () => {
      sendStartedRef.current = false;
      setGrace(false);
    },
  });

  const simulate = useMutation({
    mutationFn: (orderIndex: number) => dealsApi.simulateSignature(deal.id, orderIndex),
    onSuccess: reload,
  });

  function fireSend(simulateEnvelope: boolean) {
    if (sendStartedRef.current) return;
    sendStartedRef.current = true;
    setGrace(false);
    send.mutate(simulateEnvelope);
  }

  useEffect(() => {
    if (!grace) return;
    setSecondsLeft(ZOHO_GRACE_SECONDS);
    const started = Date.now();
    const tick = window.setInterval(() => {
      const left = Math.max(
        0,
        ZOHO_GRACE_SECONDS - Math.floor((Date.now() - started) / 1000),
      );
      setSecondsLeft(left);
    }, 250);
    const timeout = window.setTimeout(() => fireSend(false), ZOHO_GRACE_SECONDS * 1000);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(timeout);
    };
    // Intentionally only restart when the grace window opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grace]);

  // The signer who is currently expected to act, if any.
  const pending = request?.signers.find((signer) => signer.status === "sent");
  const isMock = request?.provider === "mock";

  return (
    <Panel>
      <PanelHead eyebrow={t("signature.eyebrow")} title={t("signature.title")}>
        {t("signature.intro")}
      </PanelHead>

      <div className="space-y-2.5">
        {(request?.signers ?? placeholderSigners(deal, t)).map((signer) => (
          <div
            key={signer.order_index}
            className={cn(
              "flex items-center gap-3.5 rounded-card border bg-surface px-4 py-3.5",
              signer.status === "signed" && "border-ok/45 bg-ok-wash/40",
              signer.status === "sent" && "border-teal/50 bg-teal-wash/40",
              signer.status === "declined" && "border-danger/50 bg-red-50",
              signer.status === "waiting" && "border-line",
            )}
          >
            <div
              className={cn(
                "grid size-[38px] shrink-0 place-items-center rounded-full font-display text-[13px] font-bold",
                signer.status === "signed"
                  ? "bg-ok text-white"
                  : signer.status === "sent"
                    ? "bg-teal text-white"
                    : "bg-line-soft text-muted",
              )}
            >
              {initials(signer.name)}
            </div>

            <div className="min-w-0 flex-1">
              <div className="text-[14.5px] font-semibold">{signer.name}</div>
              <div className="text-[12.5px] text-muted">{signer.role_label}</div>
              {signer.email && (
                <div className="truncate font-mono text-[11.5px] text-muted">{signer.email}</div>
              )}
            </div>

            <div className="shrink-0 text-right">
              <div
                className={cn(
                  "text-[12.5px] font-semibold",
                  signer.status === "signed" && "text-ok",
                  signer.status === "sent" && "text-teal-deep",
                  signer.status === "declined" && "text-danger",
                  signer.status === "waiting" && "text-muted",
                )}
              >
                {request ? t(`signature.status.${signer.status}`) : t("signature.notSent")}
              </div>
              {signer.signed_at && (
                <div className="text-[11.5px] text-muted">{formatDateTime(signer.signed_at)}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Shown before and after sending: the envelope goes out without these, so
          it stays visible on a signed deal rather than only warning up front. */}
      {deal.missing_signature_documents.length > 0 && (
        <Note tone="warn" className="mt-4.5">
          <b>{t("contract.missingTitle")}</b>{" "}
          {t("contract.missingBody", { names: deal.missing_signature_documents.join(", ") })}
        </Note>
      )}

      {complete && (
        <Note tone="ok" className="mt-4.5">
          <b>{t("signature.completeTitle")}</b> {t("signature.completeBody")}
        </Note>
      )}

      {request && !complete && (
        <Note tone="info" className="mt-4.5">
          {t("signature.waitingFor", { name: pending?.name ?? t("signature.nextSigner") })}
        </Note>
      )}

      {/* Mock-provider control: the real trigger is the Zoho Sign webhook. */}
      {isMock && !complete && pending && !readOnly && (
        <Note tone="warn" className="mt-3.5">
          <b>{t("signature.mockTitle")}</b> {t("signature.mockBody")}
          <Button
            variant="ghost"
            size="sm"
            className="mt-2.5"
            busy={simulate.isPending}
            onClick={() => simulate.mutate(pending.order_index)}
          >
            {t("signature.simulate", { name: pending.name })}
          </Button>
        </Note>
      )}

      {grace && !request && !readOnly && (
        <Note tone="warn" className="mt-3.5">
          <b>{t("signature.graceTitle")}</b>{" "}
          {t("signature.graceBody", { seconds: secondsLeft })}
          <Button
            variant="ghost"
            size="sm"
            className="mt-2.5"
            busy={send.isPending}
            onClick={() => fireSend(true)}
          >
            {t("signature.simulateEsign")}
          </Button>
        </Note>
      )}

      {(send.isError || simulate.isError) && (
        <Note tone="error" className="mt-3.5">
          {((send.error ?? simulate.error) as Error).message}
        </Note>
      )}

      <PanelActions>
        <Button variant="ghost" onClick={onBack}>
          {t("common.back")}
        </Button>
        <Spacer />
        {!request && !readOnly && !grace && (
          <Button variant="teal" busy={send.isPending} onClick={() => setGrace(true)}>
            {t("signature.send")}
          </Button>
        )}
        {grace && !request && <ActionHint>{t("signature.graceTitle")}</ActionHint>}
        {request && !complete && <ActionHint>{t("signature.running")}</ActionHint>}
        {complete && <Button onClick={onNext}>{t("signature.continue")}</Button>}
      </PanelActions>
    </Panel>
  );
}

/**
 * Signer rows shown before the envelope exists, so the step is never an empty
 * panel. Names come from the deal itself; the countersigner is configured
 * server-side and therefore not known here yet.
 */
function placeholderSigners(
  deal: DealDetail,
  t: ReturnType<typeof useI18n>["t"],
): Signer[] {
  return [
    {
      order_index: 0,
      name:
        deal.customer?.contact_name ||
        deal.customer?.company_name ||
        t("signature.placeholder.customer"),
      email: deal.customer?.contact_email ?? "",
      role_label: t("signature.placeholder.customerRole"),
      status: "waiting",
      sent_at: null,
      signed_at: null,
    },
    {
      order_index: 1,
      name: t("signature.placeholder.management"),
      email: "",
      role_label: t("signature.placeholder.managementRole"),
      status: "waiting",
      sent_at: null,
      signed_at: null,
    },
  ];
}
